import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  UserCheck,
  RefreshCcw,
  Headphones,
} from "lucide-react";

const items = [
  {
    title: "Verified Staff",
    desc: "Carefully screened professionals for your home.",
    icon: UserCheck,
    tone: "indigo",
  },
  {
    title: "Background Checked",
    desc: "A safer and more trusted hiring experience.",
    icon: ShieldCheck,
    tone: "sky",
  },
  {
    title: "Quick Replacement",
    desc: "Fast support if your requirement changes.",
    icon: RefreshCcw,
    tone: "orange",
  },
  {
    title: "24/7 Support",
    desc: "Guidance whenever you need help.",
    icon: Headphones,
    tone: "emerald",
  },
];

const toneStyles = {
  indigo: {
    icon: "text-indigo-600",
    line: "from-indigo-300 via-indigo-100 to-transparent",
    accent: "text-indigo-700",
  },
  sky: {
    icon: "text-sky-600",
    line: "from-sky-300 via-sky-100 to-transparent",
    accent: "text-sky-700",
  },
  orange: {
    icon: "text-orange-600",
    line: "from-orange-300 via-orange-100 to-transparent",
    accent: "text-orange-700",
  },
  emerald: {
    icon: "text-emerald-600",
    line: "from-emerald-300 via-emerald-100 to-transparent",
    accent: "text-emerald-700",
  },
};

const Trust = () => {
  return (
    <section className="relative px-4 py-20 md:py-28 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,#faf8ff_0%,#f4f0ff_42%,#fdfbff_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_14%_18%,rgba(99,102,241,0.10),transparent_22%),radial-gradient(circle_at_84%_18%,rgba(168,85,247,0.08),transparent_20%),radial-gradient(circle_at_80%_82%,rgba(56,189,248,0.06),transparent_22%)]" />
      <div className="absolute -top-24 left-[-70px] w-72 h-72 bg-indigo-200/20 rounded-full blur-3xl" />
      <div className="absolute top-[10%] right-[-90px] w-80 h-80 bg-purple-200/18 rounded-full blur-3xl" />
      <div className="absolute bottom-[-110px] left-[12%] w-64 h-64 bg-sky-200/18 rounded-full blur-3xl" />
      <div className="absolute inset-0 opacity-[0.03] [background-image:radial-gradient(#0f172a_0.8px,transparent_0.8px)] [background-size:22px_22px]" />

      {/* Top / bottom soft rails */}
      <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-white/45 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white/45 to-transparent" />

      <div className="relative max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-14 md:mb-18"
        >
          <span className="inline-flex items-center rounded-full border border-indigo-200 bg-white/80 backdrop-blur px-4 py-2 text-sm text-indigo-700 font-medium mb-5 shadow-sm">
            Trust, Safety & Support
          </span>

          <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-[1.02] mb-4">
            Why Families Choose A2Z
          </h2>

          <p className="text-gray-600 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            Built around reliability, safer hiring, and support that stays with you throughout the process.
          </p>
        </motion.div>

        {/* Editorial pillars */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-y-12 sm:gap-y-14 xl:gap-y-0 xl:gap-x-10">
          {items.map((item, i) => {
            const Icon = item.icon;
            const tone = toneStyles[item.tone];

            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                viewport={{ once: true }}
                className="relative"
              >
                {/* Vertical divider desktop */}
                {i !== items.length - 1 && (
                  <div className="hidden xl:block absolute top-2 right-[-20px] w-px h-[82%] bg-gradient-to-b from-transparent via-slate-200 to-transparent" />
                )}

                <div className="text-center xl:text-left">
                  <div className="flex justify-center xl:justify-start mb-5">
                    <div className="relative">
                      <div className={`w-14 h-14 md:w-16 md:h-16 rounded-full bg-white/75 backdrop-blur border border-white/80 shadow-sm flex items-center justify-center ${tone.icon}`}>
                        <Icon className="w-7 h-7" />
                      </div>
                      <div className={`absolute -bottom-3 left-1/2 -translate-x-1/2 xl:left-0 xl:translate-x-0 w-20 h-[2px] bg-gradient-to-r ${tone.line}`} />
                    </div>
                  </div>

                  <div className={`text-xs uppercase tracking-[0.22em] font-semibold mb-3 ${tone.accent}`}>
                    0{i + 1}
                  </div>

                  <h3 className="text-2xl md:text-3xl font-semibold text-gray-900 leading-tight mb-3">
                    {item.title}
                  </h3>

                  <p className="text-sm md:text-base text-gray-600 leading-relaxed max-w-[280px] mx-auto xl:mx-0">
                    {item.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Trust;